import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { firebaseAuth, googleProvider } from './firebase';
import { setAuthToken } from '@/api/client';
import { e2eApiToken } from './e2eApiToken';
import { useAuthStore, type AuthUser, type UserRole } from '@/stores/auth';
import { breadcrumb, setSentryUser } from './sentry';

interface AuthCtx {
  user: User | null;
  loading: boolean;
  /// True quando Firebase foi inicializada (env presente). Em CI / dev
  /// sem `.env.local`, é false — UI deve mostrar mensagem amigável em
  /// vez de chamar os métodos abaixo (eles lançam erro claro se forem
  /// invocados nesse estado).
  configured: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const KNOWN_ROLES: ReadonlySet<UserRole> = new Set([
  'organizer',
  'verified_organizer',
  'builder',
  'athlete',
  'support',
]);

function parseRoles(claim: unknown): UserRole[] {
  if (!Array.isArray(claim)) return [];
  return claim.filter((r): r is UserRole => typeof r === 'string' && KNOWN_ROLES.has(r as UserRole));
}

// Ativado quando Playwright injeta VITE_E2E_TEST_MODE=true no webServer.
// Nunca deve ser 'true' em builds de produção — a env var simplesmente
// não existe nesse contexto.
const E2E_TEST_MODE = import.meta.env.VITE_E2E_TEST_MODE === 'true';

const NOT_CONFIGURED_ERROR = new Error(
  'Firebase auth não está configurada. Defina VITE_FIREBASE_* em ' +
    'infra/dashboard/admin/.env.local (PERMISSIONS_CHECKLIST.md).',
);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const setStoreUser = useAuthStore((s) => s.setUser);

  useEffect(() => {
    // Modo E2E: Playwright injeta window.__E2E_USER__ via addInitScript antes
    // da navegação. Se presente, bypassa Firebase completamente. Sem ele, o
    // usuário fica não-autenticado (AuthGuard redireciona pra /login) — o que
    // permite testar os fluxos de redirect sem auth real.
    if (E2E_TEST_MODE) {
      const w = window as unknown as { __E2E_USER__?: AuthUser };
      if (w.__E2E_USER__) {
        // `VITE_E2E_API_TOKEN=loadtest` troca o token fixo pelo de
        // desenvolvimento, pra rodar contra a API local real (`lib/e2eApiToken`).
        setAuthToken(e2eApiToken(w.__E2E_USER__, import.meta.env.VITE_E2E_API_TOKEN));
        setStoreUser(w.__E2E_USER__);
        setSentryUser(w.__E2E_USER__.uid);
        // {} as User é suficiente — AuthGuard só checa truthiness (if (!user)).
        setUser({} as User);
      } else {
        setAuthToken(null);
        setStoreUser(null);
        setSentryUser(null);
      }
      setLoading(false);
      return;
    }

    // Sem Firebase configurada, marca loading=false imediato e fica em
    // modo no-auth. Rotas guarded vão redirecionar pra /login (que mostra
    // o erro). Rotas públicas (`/`, `/privacy`, `/live`) renderizam
    // normalmente — esse é o caminho exercitado pelos e2e em CI.
    if (!firebaseAuth) {
      setAuthToken(null);
      setStoreUser(null);
      setSentryUser(null);
      setLoading(false);
      return;
    }

    // onIdTokenChanged dispara em login, logout E em refresh do token
    // (Firebase rotaciona a cada ~1h). Usamos só pra espelhar o usuário/roles
    // no store e no Sentry — a renovação do token em si é responsabilidade do
    // modelo pull em client.ts (`user.getIdToken()` por request).
    const unsub = onIdTokenChanged(firebaseAuth, async (fbUser) => {
      setUser(fbUser);
      if (fbUser) {
        const result = await fbUser.getIdTokenResult();
        // NÃO chamar setAuthToken aqui. O `client.ts` usa modelo pull:
        // cada request chama `user.getIdToken()`, que devolve o cache
        // enquanto válido e AUTO-RENOVA na expiração. Empurrar o token pro
        // override aqui congelava um snapshot de ~1h — quando o
        // onIdTokenChanged não refrescava a tempo (aba em background), as
        // requests saíam com token expirado → 401 esporádico. O override
        // é exclusivo de E2E/bootstrap (ver comentário em client.ts).
        setStoreUser({
          uid: fbUser.uid,
          email: fbUser.email ?? '',
          displayName: fbUser.displayName,
          roles: parseRoles(result.claims.roles),
        });
        setSentryUser(fbUser.uid);
        breadcrumb('id token changed', 'auth', {
          roles: parseRoles(result.claims.roles).join(',') || 'none',
        });
      } else {
        setAuthToken(null);
        setStoreUser(null);
        setSentryUser(null);
        breadcrumb('signed out', 'auth');
      }
      setLoading(false);
    });
    return unsub;
  }, [setStoreUser]);

  const value: AuthCtx = {
    user,
    loading,
    configured: E2E_TEST_MODE || firebaseAuth !== null,
    signInWithGoogle: async () => {
      if (!firebaseAuth || !googleProvider) throw NOT_CONFIGURED_ERROR;
      await signInWithPopup(firebaseAuth, googleProvider);
    },
    signInWithEmail: async (email, password) => {
      if (!firebaseAuth) throw NOT_CONFIGURED_ERROR;
      await signInWithEmailAndPassword(firebaseAuth, email, password);
    },
    signOut: async () => {
      if (!firebaseAuth) throw NOT_CONFIGURED_ERROR;
      await signOut(firebaseAuth);
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
